import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { useIsFeedbackOnFeedbackEnabled } from '@modules/feedback/hooks';
import { useFetchTenantAutoApprovalState } from '@modules/users/hooks';
import { useFetchUsers } from '@modules/users/hooks/useFetchUsers';
import { ApprovedDomains } from '@modules/workspaceSettings/components/ApprovedDomains';
import { SSOSettings } from '@modules/workspaceSettings/components/SSOSettings';
import { TaxonomySettings } from '@modules/workspaceSettings/components/TaxonomySettings';
import { Users } from '@modules/workspaceSettings/components/Users';
import { ArrowBackRounded } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { Navigate, useNavigate } from 'react-router-dom';

interface WorkspaceSettingsProps {
  isOnboardingPageVariant?: boolean;
}

export const WorkspaceSettings = ({
  isOnboardingPageVariant,
}: WorkspaceSettingsProps) => {
  const canAccessWorkspaceSettings = useIsAuthorized({
    resource: PermissionResource.WorkspaceSettings,
    operation: PermissionOperation.Write,
  });

  const isFeedbackOnFeedbackEnabled = useIsFeedbackOnFeedbackEnabled();

  const shouldSkipAPICalls = !canAccessWorkspaceSettings;

  useFetchUsers({
    skip: shouldSkipAPICalls,
  });

  useFetchTenantAutoApprovalState({
    skip: shouldSkipAPICalls,
  });

  const navigate = useNavigate();

  if (!canAccessWorkspaceSettings) {
    return <Navigate replace to="/" />;
  }

  return (
    <Stack spacing={5} px={isOnboardingPageVariant ? 16 : 3} py={4}>
      <Stack direction="row" spacing={2} alignItems="center">
        {isOnboardingPageVariant && (
          <IconButton
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowBackRounded
              fontSize="large"
              sx={{
                color: 'text.secondary',
              }}
            />
          </IconButton>
        )}
        <Typography variant="h4">Workspace Settings</Typography>
      </Stack>
      <Stack spacing={4}>
        <SSOSettings />
        {isFeedbackOnFeedbackEnabled && <TaxonomySettings />}
        <ApprovedDomains />
        <Users />
      </Stack>
    </Stack>
  );
};
